import { FieldOption } from 'module/api/field/types';
import { User } from 'module/api/user/types';
import { fullName } from 'module/helpers/full-name';
import { getRandomColor } from 'module/helpers/get-random-color';
import { FieldType } from 'module/types/filter-field/field/field';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { humanise } from 'module/ui/components/atoms/humanised-date';
import { Tag } from '@rocketlane/ui';
import { CustomNodeResponseFieldValueType } from 'api/custom-section/types';
import DOMPurify from 'dompurify';
import { isArray, isEmpty, memoize } from 'lodash';
import moment from 'moment';

type Props = {
  field: FilterableField;
  value: CustomNodeResponseFieldValueType;
};
const formatDate = memoize((value: string) =>
  value == null ? '-' : humanise(moment(value)),
);
const renderSingleSelect = (option: FieldOption<number>) => (
  <Tag
    key={option.value}
    color={getRandomColor(option.value) as any}
    variant="basic"
  >
    {option.label}
  </Tag>
);
const renderMultiSelect = (options: FieldOption<number>[]) =>
  isArray(options) ? (
    <>{options.map((option) => renderSingleSelect(option))}</>
  ) : (
    '-'
  );

const renderSingleUser = (user: User) => {
  const key = user.userId ?? user.fullName ?? user.emailId;
  const userFullName = fullName(user);
  const nameToDisplay = isEmpty(userFullName)
    ? (user.fullName ?? user.emailId)
    : userFullName;

  return (
    <Tag key={key} color={getRandomColor(key) as any} variant="basic">
      {nameToDisplay}
    </Tag>
  );
};
const renderMultiUser = (users: User[]) =>
  isArray(users) ? <>{users.map((user) => renderSingleUser(user))}</> : '-';

export const getFieldRenderer = ({ field, value }: Props): React.ReactNode => {
  switch (field.type) {
    case FieldType.Boolean:
      return <>{value != null ? (value ? 'Yes' : 'No') : '-'}</>;
    case FieldType.Number:
      return <>{value ?? '-'}</>;
    case FieldType.Text:
      return <>{value ?? '-'}</>;
    case FieldType.MultiLineText:
      return value ? (
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(value as string),
          }}
        />
      ) : (
        '-'
      );
    case FieldType.Date:
      return <>{formatDate(value as string) ?? '-'}</>;
    case FieldType.SingleSelect:
      return value ? renderSingleSelect(value as FieldOption<number>) : '-';
    case FieldType.MultiSelect:
      return value ? renderMultiSelect(value as FieldOption<number>[]) : '-';
    case FieldType.SingleUser:
      return value ? renderSingleUser(value as User) : '-';
    case FieldType.MultiUser:
      return value ? renderMultiUser(value as User[]) : '-';
  }
};
